import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Loader2, FileX, Maximize2 } from 'lucide-react';
import axiosInstance from '../../api/axios';
import { buildAnnotationPanels, buildCitationPanels, panelCaption } from './annotationPanels';

/**
 * The annotated documents behind one line item: the source (statement line or population row) and every
 * page or row it matched, side by side, with the matching text marked. PDFs with a text layer are marked
 * exactly from their text (no AI); spreadsheet rows come from the row screenshot; a page with no text layer
 * falls back to the AI annotator, then to the plain page.
 */

const METHOD_LABEL = { text: 'text match', rows: 'row highlighted', ai: 'AI annotation', saved: 'AI annotation', original: 'original page' };
const SUPPORTED_STATUS = (r) => r.status === 200 && r.data && r.data.byteLength > 0;
const toUrl = (data) => URL.createObjectURL(new Blob([data], { type: 'image/png' }));

/** { url, headers } of an image response, or null when there is none (204, an error, an empty body). */
async function post(path, body, timeout) {
  try {
    const resp = await axiosInstance.post(path, body, { responseType: 'arraybuffer', timeout });
    return SUPPORTED_STATUS(resp) ? { url: toUrl(resp.data), headers: resp.headers || {} } : null;
  } catch {
    return null;
  }
}

/** "x0,y0,x1,y1" (fractions of the page) -> { y0, y1 } or null */
export function parseHighlightBox(headers) {
  const raw = headers?.['x-highlight-box'];
  const v = String(raw || '').split(',').map(Number);
  return v.length === 4 && v.every(Number.isFinite) ? { y0: v[1], y1: v[3] } : null;
}

const isDocumentFile = (name) => /\.(pdf|png|jpe?g|webp|gif|bmp|tiff?|heic|heif)$/i.test(String(name || ''));

/** { url, method, box } for one panel (box: where the first highlight is), or null when nothing could be shown. */
export async function loadPanelImage(panel, runId) {
  if (!panel.fileId) return null;
  const citation = {
    source_file: panel.file, source_file_id: panel.fileId, sheet_name: panel.sheet, page_number: panel.page,
    // the row screenshot counts data rows below a header on row 1
    row_numbers: panel.row != null ? [Math.max(1, panel.row - 1)] : null, excerpt: (panel.excerpt || '').slice(0, 200),
  };
  const base = { file_id: panel.fileId, filename: panel.file, citation, execution_id: runId ? String(runId) : null };

  // A page the run already annotated when it finished (saved under the run): no AI call, shown at once.
  if (panel.saved && runId) {
    const saved = await post('/api/evidence/saved-annotation', { ...base, citation: panel.saved }, 30000);
    if (saved) return { url: saved.url, method: 'saved', box: null };
  }

  // A line of a PDF or image population (a scanned statement) is located on its page: it has a row number but no
  // page, and only spreadsheets are shown as rows.
  const page = panel.page ?? (isDocumentFile(panel.file) && panel.targets?.length ? 1 : null);
  if (page != null) {
    // A line item's values are marked exactly from the page's text first; a sample's citations get the AI annotation
    // first (its drawn notes say more), with the text marks and their result tags standing in when it fails.
    const highlight = async () => {
      const marked = panel.targets?.length ? await post('/api/evidence/highlight', {
        file_id: panel.fileId, filename: panel.file, page_number: page, targets: panel.targets, execution_id: runId ? String(runId) : null,
      }, 60000) : null;
      return marked && { url: marked.url, method: 'text', box: parseHighlightBox(marked.headers) };
    };
    const annotate = async () => {
      const ai = await post('/api/evidence/annotate', { ...base, citation: { ...citation, page_number: page }, findings_text: panel.findings || '' }, 90000);
      return ai && { url: ai.url, method: 'ai', box: null };
    };
    for (const step of panel.aiFirst ? [annotate, highlight] : [highlight, annotate]) {
      const got = await step();   // eslint-disable-line no-await-in-loop
      if (got) return got;
    }
  }
  const plain = await post('/api/evidence/screenshot', base, 60000);
  return plain ? { url: plain.url, method: panel.row != null ? 'rows' : 'original', box: null } : null;
}

function Panel({ panel, runId }) {
  const [state, setState] = useState({ loading: true, url: null, method: null, box: null });
  const urlRef = useRef(null);
  const scrollRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, url: null, method: null, box: null });
    loadPanelImage(panel, runId).then((res) => {
      if (cancelled) { if (res?.url) URL.revokeObjectURL(res.url); return; }
      urlRef.current = res?.url || null;
      setState({ loading: false, url: res?.url || null, method: res?.method || null, box: res?.box || null });
    });
    return () => { cancelled = true; if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; } };
  }, [panel, runId]);

  // Scroll the page so its first highlight is in view (a tall page would otherwise hide it below the fold).
  const showHighlight = (e) => {
    const box = state.box;
    const holder = scrollRef.current;
    if (!box || !holder) return;
    const h = e.currentTarget.clientHeight;
    holder.scrollTop = Math.max(0, h * ((box.y0 + box.y1) / 2) - holder.clientHeight / 2);
  };

  const source = panel.role === 'source';
  return (
    <div className="min-w-0 border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid={`panel-${panel.key}`}>
      <div className="flex items-center gap-2 px-3 py-1.5 text-[11px] bg-gray-50 border-b border-gray-200">
        <span className="truncate flex-1 text-gray-600" title={panelCaption(panel)}>{panelCaption(panel)}</span>
        {state.method && <span className="text-gray-400">{METHOD_LABEL[state.method]}</span>}
        <span
          className={`px-1.5 py-0.5 rounded ${source ? 'bg-indigo-50 text-indigo-700' : panel.mapped ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-700'}`}
          title={panel.mapped ? 'Mapped to this sample; the AI did not cite a page of it' : undefined}
        >
          {source ? 'Source' : panel.mapped ? 'Mapped, not cited' : 'Evidence'}
        </span>
        {state.url && (
          <button type="button" onClick={() => window.open(state.url, '_blank', 'noopener')} title="Open full size" className="text-indigo-600 hover:text-indigo-800">
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {/* A plain block scroll box: centring with flex inside an overflow box cuts off the top of a tall page. */}
      <div ref={scrollRef} className="bg-gray-50 min-h-[200px] max-h-[460px] overflow-auto p-2" data-testid={`panel-scroll-${panel.key}`}>
        {state.loading && <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-indigo-400" aria-label="Loading" /></div>}
        {!state.loading && state.url && <img src={state.url} alt={panelCaption(panel)} onLoad={showHighlight} className="block mx-auto max-w-full h-auto rounded shadow-sm" />}
        {!state.loading && !state.url && (
          <div className="text-center text-xs text-gray-400 py-16"><FileX className="h-5 w-5 mx-auto mb-1" />{panel.fileId ? 'No preview available' : 'Preview not available for this run — re-run to enable'}</div>
        )}
      </div>
    </div>
  );
}

/** The worst result of a sample's attributes, for a sample shown without a line item. */
function sampleStatus(results) {
  const s = (results || []).map((r) => r.status);
  if (s.includes('failed')) return 'failed';
  if (s.length && s.every((x) => x === 'passed')) return 'passed';
  return 'warning';
}

export default function LineItemAnnotationViewer({ lines, index, onIndex, runId, onClose }) {
  const line = lines[index];
  // A line item shows its source and matches; a sample without line items shows what its attributes cite.
  const { panels, hiddenEvidence } = useMemo(
    () => (line?.item ? buildAnnotationPanels(line.item) : buildCitationPanels(line?.results, line?.fileRefs)),
    [line],
  );
  const go = useCallback((d) => {
    const n = index + d;
    if (n >= 0 && n < lines.length) onIndex(n);
  }, [index, lines.length, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  if (!line) return null;
  const status = line.item ? line.item.status : sampleStatus(line.results);
  const observation = line.item ? line.item.observation : null;
  const tone = status === 'passed' ? 'text-green-700 bg-green-50' : status === 'failed' ? 'text-red-700 bg-red-50' : 'text-amber-700 bg-amber-50';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-evidence-viewer-open role="dialog" aria-label="Annotated evidence">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-full flex flex-col">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <div className="min-w-0 flex-1">
            <p className="text-sm text-gray-800 truncate">
              <span className="font-medium">Item {index + 1} of {lines.length}</span>
              <span className="text-gray-400"> · {line.sampleRef} · Attribute{(line.attrs || []).length > 1 ? 's' : ''} {(line.attrs || []).map((a) => a.ref).join(', ')}</span>
            </p>
            {line.item
              ? <p className="text-xs font-mono text-gray-600 truncate" title={line.item.item_ref}>{line.item.item_ref}</p>
              : <p className="text-xs text-gray-500">No line items: showing where each attribute's result is evidenced</p>}
          </div>
          <span className={`text-[11px] px-2 py-0.5 rounded ${tone}`}>{status === 'passed' ? 'Passed' : status === 'failed' ? 'Failed' : 'Inconclusive'}</span>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {observation && <p className="text-xs text-gray-600 mb-3">{observation}</p>}
          {panels.length === 0 ? (
            <p className="text-sm text-gray-400 italic py-8 text-center">No documents are linked to this {line.item ? 'line item' : 'sample'}.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {panels.map((p) => <Panel key={`${line.item?.item_ref ?? line.sampleRef}-${p.key}`} panel={p} runId={runId} />)}
            </div>
          )}
          {hiddenEvidence > 0 && <p className="text-[11px] text-gray-400 mt-2">+{hiddenEvidence} more matching page(s) not shown.</p>}
        </div>
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50 rounded-b-xl text-xs text-gray-500">
          <span><span className="text-green-700">Green</span> = agrees · <span className="text-red-600">Red</span> = mismatch or not supported · <span className="text-amber-600">Amber</span> = could not be assessed</span>
          <span className="flex items-center gap-2">
            <button type="button" onClick={() => go(-1)} disabled={index === 0} className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded bg-white disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" />Previous item</button>
            <button type="button" onClick={() => go(1)} disabled={index >= lines.length - 1} className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded bg-white disabled:opacity-40">Next item<ChevronRight className="h-3.5 w-3.5" /></button>
          </span>
        </div>
      </div>
    </div>
  );
}
